/* biome-ignore-all lint/suspicious/noExplicitAny: legacy data structure */
import { FolderOutlined, BookOutlined } from "@ant-design/icons"
import type { TablePaginationConfig, TableProps } from "antd"
import { Table, Tag, Typography, Button, Tooltip } from "antd"
import { UserCellLink } from "@/components/UserCellLink"

const { Text } = Typography
import type React from "react"
import { useTableScroll } from "@/hooks/useTableScroll"
import "@/styles/table.full.css"
import { formatDateTime } from "@/utils/dateUtils"

interface SetTableProps {
  data: any[]
  loading: boolean
  selectedRowKeys: React.Key[]
  onSelectionChange: (selectedRowKeys: React.Key[], selectedRows: any[]) => void
  onViewVocabularies?: (record: any) => void
  pagination?: TableProps["pagination"]
}

const SetTable: React.FC<SetTableProps> = ({
  data,
  loading,
  selectedRowKeys,
  onSelectionChange,
  onViewVocabularies,
  pagination,
}) => {
  const { scrollConfig, tableWrapperRef } = useTableScroll()

  const columns: TableProps["columns"] = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      fixed: "left",
      width: 85,
      align: "center",
      render: (value: number) => <span className="font-semibold">{value}</span>,
    },
    {
      title: "Bộ từ",
      key: "set_info",
      width: 250,
      fixed: "left",
      render: (_: any, record: any) => (
        <div className="flex flex-col gap-0.5">
          <div className="group flex cursor-pointer items-center gap-2" onClick={() => onViewVocabularies?.(record)}>
            <FolderOutlined style={{ color: record.color || "#1677ff" }} />
            <span className="font-bold text-primary group-hover:underline">{record.title}</span>
          </div>
          {record.description && (
            <div className="line-clamp-2">
              <Text type="secondary" className="text-xs">
                {record.description}
              </Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Người tạo",
      key: "owner",
      width: 200,
      render: (_: any, record: any) => {
        if (record.type === "system") {
          return <Tag color="blue">Hệ thống</Tag>
        }
        return <UserCellLink user={record.user} userId={record.user_id} />
      },
    },
    {
      title: "Hiển thị",
      dataIndex: "visibility",
      key: "visibility",
      width: 100,
      align: "center",
      render: (value: string) => {
        if (value === "public") {
          return <Tag color="success">Công khai</Tag>
        }
        return <Tag color="default">Riêng tư</Tag>
      },
    },
    {
      title: "Mặc định",
      dataIndex: "is_default",
      key: "is_default",
      width: 100,
      align: "center",
      render: (value: number) => {
        if (value === 1) {
          return <Tag color="cyan">Mặc định</Tag>
        }
        return <Text type="secondary">—</Text>
      },
    },
    {
      title: "Số từ",
      dataIndex: "vocabulary_count",
      key: "vocabulary_count",
      width: 110,
      align: "center",
      render: (value: number, record: any) => (
        <Tooltip title="Bấm để xem danh sách từ vựng trong bộ này">
          <Button
            type="link"
            size="small"
            icon={<BookOutlined />}
            onClick={() => onViewVocabularies?.(record)}
            className="inline-flex items-center p-0 font-bold text-blue-600 hover:underline"
          >
            {value ?? 0} từ
          </Button>
        </Tooltip>
      ),
    },
    {
      title: "Ngày tạo",
      dataIndex: "created_at",
      key: "created_at",
      width: 130,
      align: "center",
      render: (value: string) => (value ? formatDateTime(value, "dd/mm/yy hh:mm") : "—"),
    },
  ]

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectionChange,
  }

  return (
    <div className="min-h-0 min-w-0 flex-1" ref={tableWrapperRef}>
      <Table
        bordered
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        rowSelection={rowSelection}
        pagination={pagination as TablePaginationConfig}
        scroll={{ x: "100%", y: scrollConfig.y }}
        size="small"
        classNames={{
          root: "full-height-table",
        }}
      />
    </div>
  )
}

SetTable.displayName = "SetTable"

export default SetTable
